import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState, Suspense } from 'react';
import { useAuthContext } from 'AuthContext';
import Card from 'components/Card';
import CommentForm from 'components/CommentForm';
import CommunityCard from 'containers/cards/Community';
import PageSubHeading from 'containers/Page/SubHeading';
import {
  useApolloClient,
  useMutation,
  useQuery,
  useLazyQuery,
} from '@apollo/client';
import { useRouter } from 'found';
import { useTheme } from '@mui/material';
import useIsMobile from 'lib/useIsMobile';
import CommunityPage from './Page';
import { GET_FETCH_SPONSOR_DETAILS_INFO } from '../../schema/apolloQueries/communityDetailsQueries';
import {
  JOIN_LEAVE_SPONSOR,
  LIKED_UNLIKED_SPONSOR,
} from '../../schema/apolloMutations/eventDeatilsMutations';
import { GET_ACTIVITY_GROUP } from '../../schema/apolloQueries/eventDeatilsQueries';
import { GET_PROFILE_INFO } from '../../schema/apolloQueries/commonQueries';
import apiGateway from '../../lib/apiGateway';
import SponsorMessage from './SponsorMessage';

const Feed = React.lazy(() => import('feed/Feed'));

const styles = {
  feedCard: { marginBottom: 10 },
  mainCard: { marginBottom: 20 },
};

const CommunityDetailsPage = () => {
  const [isAlert, setAlert] = useState(false);

  const {
    match: {
      params: { communitySlug },
    },
  } = useRouter();

  const { data, error, loading } = useQuery(GET_FETCH_SPONSOR_DETAILS_INFO, {
    variables: {
      slug: encodeURIComponent(communitySlug),
    },
  });

  useEffect(() => {
    if (
      !loading
      && error
      && JSON.parse(error?.message)
      && JSON.parse(error?.message)?.status === 'NOT_FOUND'
    ) {
      setAlert(true);
    }
  }, [loading, error]);

  const client = useApolloClient();

  const profileData = client.readQuery({
    query: GET_PROFILE_INFO,
  });

  const [handleLike] = useMutation(LIKED_UNLIKED_SPONSOR, {
    variables: {
      model: 'sponsor',
    },
  });

  const [handleJoin] = useMutation(JOIN_LEAVE_SPONSOR, {
    refetchQueries: [GET_FETCH_SPONSOR_DETAILS_INFO],
  });

  const [getActivityGroup, { data: activityGroupData }] = useLazyQuery(
    GET_ACTIVITY_GROUP,
    {
      variables: {
        eventId: 0,
      },
    },
  );

  const personId = profileData?.getProfile?.personId
    ? profileData?.getProfile?.personId
    : null;

  useEffect(() => {
    if (personId) {
      getActivityGroup();
    }
  }, [personId]);

  const isMobile = useIsMobile();
  const { showSignInDialog } = useAuthContext();
  const theme = useTheme({ isMobile });
  const feedRef = useRef(null);
  const [feedMF, setFeedMF] = useState(null);
  const [creatingComment, setCreatingComment] = useState(false);

  const onCreate = (
    content,
    {
      resetForm = () => {},
      setErrors = () => {},
      setSubmitting = () => {},
    } = {},
  ) => {
    setCreatingComment(true);
    const { getActivityGroup: getAG } = activityGroupData;
    const sponsorId = data?.fetchSponsorDetails?.id;
    const task = getAG[0].activities[0]?.tasks.find(
      tsk => tsk.name === 'DISCUSSION',
    );
    const conversation = {
      activityGroupId: getAG[0].id || '',
      activityId: getAG[0].activities[0]?.id || '',
      body: content.body,
      eventId: 0,
      productId: 0,
      productName: 'Sponsor',
      sponsorId,
      taskId: task?.id || '',
      userId: personId,
    };
    apiGateway
      .joinConversation(conversation)
      .then(res => {
        if (res) {
          setSubmitting(false);
          setFeedMF(null);
          setCreatingComment(false);
        }
      })
      .catch(e => {
        setErrors(e);
        setSubmitting(false);
        setCreatingComment(false);
      });
    resetForm();
  };
  useEffect(() => {
    if (
      feedRef.current
      && !feedMF
      && data?.fetchSponsorDetails?.id
      && profileData
    ) {
      setFeedMF(
        <Suspense fallback='Loading Feed...'>
          <Feed
            context={{
              modelType: 'Sponsor',
              sponsorId: data?.fetchSponsorDetails?.id,
              personId: profileData?.getProfile?.personId,
              loggedIn: profileData?.getProfile?.loggedIn,
              email: profileData?.getProfile?.personInfo?.email,
              showSignInDialog,
              enabledFeedTypes: [
                {
                  type: 'SocialAsset',
                  label: 'Photos & Videos',
                },
                'Discussion',
                'Review',
              ],
            }}
            themeType={theme.palette.mode}
          />
        </Suspense>,
      );
    }
  }, [
    data?.fetchSponsorDetails,
    showSignInDialog,
    profileData,
    feedMF,
    feedRef,
    theme.palette.mode,
  ]);

  if (isAlert) {
    return (
      <SponsorMessage
        open={isAlert}
        onClick={() => {
          window.location.replace('/discover/communities');
        }}
      />
    );
  }
  return (
    <CommunityPage
      handleLike={handleLike}
      community={data?.fetchSponsorDetails}
      viewer={profileData?.getProfile}
      handleJoin={handleJoin}
    >
      <CommunityCard
        handleLike={handleLike}
        assetKey='asset'
        assetTypeKey='assetType'
        community={data?.fetchSponsorDetails}
        compact={false}
        style={styles.mainCard}
        viewer={profileData?.getProfile}
      />
      <PageSubHeading>Feed</PageSubHeading>
      <Card style={styles.feedCard}>
        <CommentForm
          //  first={first}
          personId={personId}
          first={false}
          onSave={onCreate}
          saving={creatingComment}
        />
      </Card>
      <div
        ref={feedRef}
        style={{ width: '100%', minHeight: 300, margin: '0px 0 20px' }}
      >
        {feedMF}
      </div>
    </CommunityPage>
  );
};

CommunityDetailsPage.propTypes = {
  relay: PropTypes.object.isRequired,
};

export default CommunityDetailsPage;
